// Phase JA.3 — Performance Header.
//
// Sits above the direction panel on DailyAccaPage. Reports today's
// signals_published count, rolling 7d settled win rate, the live
// streak vs longest, and average conviction.
//
// Doctrine:
//   - Anon-readable (data via useDailyAccaIntel).
//   - No fabrication: when today's row hasn't landed yet (settlement
//     job runs at 23:30 WAT), we show an honest empty-state line and
//     a Countdown to the next settlement window.
//   - Promotion-not-restriction tone — streak framing celebrates
//     discipline, not risk.

import { useMemo } from "react";
import { Badge, Eyebrow, HairlineRow, Stat, StrengthDots, Countdown } from "../super-acca";
import type {
  DailyAccaPerformanceRow,
  DailyAccaStreakRow,
} from "../../free-tier/useDailyAccaIntel.ts";

export interface PerformanceHeaderProps {
  performance: DailyAccaPerformanceRow | null;
  rollingWinRate7d: number | null;
  streak: DailyAccaStreakRow | null;
  loading?: boolean;
  /** Override "now" for tests / Storybook. Defaults to live clock. */
  nowISO?: string;
}

/** Pure: 23:30 WAT (UTC+1 → 22:30 UTC) on the same UTC date. */
export function nextSettlementISO(now: Date = new Date()): string {
  const target = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      22,
      30,
      0,
      0,
    ),
  );
  if (target.getTime() <= now.getTime()) {
    target.setUTCDate(target.getUTCDate() + 1);
  }
  return target.toISOString();
}

/** Pure: convert a 0..1 win_rate to "62%" string, or em-dash. */
export function formatWinRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return "—";
  return `${Math.round(rate * 100)}%`;
}

/** Pure: dot count out of 5 reflecting current_streak / longest_streak. */
export function streakDots(current: number, longest: number): number {
  if (longest <= 0) return 0;
  const ratio = current / longest;
  return Math.max(0, Math.min(5, Math.round(ratio * 5)));
}

export function PerformanceHeader({
  performance,
  rollingWinRate7d,
  streak,
  loading,
  nowISO,
}: PerformanceHeaderProps): JSX.Element {
  const settleISO = useMemo(
    () => nextSettlementISO(nowISO ? new Date(nowISO) : new Date()),
    [nowISO],
  );

  const todayLabel = useMemo(() => {
    const d = nowISO ? new Date(nowISO) : new Date();
    return d.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).toUpperCase();
  }, [nowISO]);

  const dots = streak ? streakDots(streak.current_streak, streak.longest_streak) : 0;
  const winRate = formatWinRate(rollingWinRate7d);
  const avgConv = performance?.avg_conviction;
  const published = performance?.signals_published ?? 0;
  const haveToday = performance !== null;

  return (
    <section
      aria-label="Daily Acca Performance Header"
      data-testid="performance-header"
      style={{
        margin: "0 auto",
        maxWidth: 1180,
        padding: "20px 48px 8px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <Eyebrow>Performance · {todayLabel}</Eyebrow>
        {loading ? <Badge variant="ghost">SYNC</Badge> : null}
      </div>

      <HairlineRow first columns="repeat(4, minmax(140px, 1fr))" padY={16}>
        <Stat
          label="Signals Today"
          value={haveToday ? String(published) : "—"}
          display
          sub={haveToday ? "published" : "after 09:00 WAT"}
        />
        <Stat
          label="7-Day Win Rate"
          value={winRate}
          display
          accent={rollingWinRate7d !== null && rollingWinRate7d >= 0.6}
          sub="settled · rolling"
        />
        <Stat
          label="Avg Conviction"
          value={avgConv !== null && avgConv !== undefined ? avgConv.toFixed(1) : "—"}
          display
          sub="today · 0–100"
        />
        <div>
          <div
            style={{
              fontFamily: "var(--sa-font-stat)",
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--sa-fg-muted)",
              textTransform: "uppercase",
            }}
          >
            Streak
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6 }}>
            <span
              style={{
                fontFamily: "var(--sa-font-stat)",
                fontSize: 30,
                lineHeight: 1,
                color: "var(--sa-fg-primary)",
              }}
            >
              {streak ? streak.current_streak : "—"}
            </span>
            <span
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 11,
                color: "var(--sa-fg-muted)",
              }}
            >
              / {streak ? streak.longest_streak : "—"} best
            </span>
          </div>
          <div style={{ marginTop: 8 }}>
            <StrengthDots n={dots} total={5} />
          </div>
        </div>
      </HairlineRow>

      {!haveToday ? (
        <div
          style={{
            marginTop: 12,
            padding: "12px 14px",
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            background: "var(--sa-bg-elevated, transparent)",
            display: "flex",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: "var(--sa-font-body)",
              fontSize: 13,
              color: "var(--sa-fg-secondary)",
            }}
          >
            Performance updates after 23:30 WAT settlement.
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.22em",
              color: "var(--sa-fg-muted)",
              textTransform: "uppercase",
            }}
          >
            Next settlement
          </span>
          <Countdown targetISO={settleISO} compact />
        </div>
      ) : null}
    </section>
  );
}
